import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, ArrowLeft, MessageSquare, Calendar } from 'lucide-react';
import {
  Main,
  ContentContainer,
  HeaderContainer,
  HeaderTop,
  Logo,
  ButtonGroup,
  ActionButton,
  HeaderSection,
  HeaderContent,
  LogoCircle,
  Title,
  Subtitle,
  InfoBadge,
  MessagesScrollContainer,
  MessageContainer,
  MessageWrapper,
  MessageBubble,
  TimeStamp,
  LoadingContainer,
  EmptyState,
} from './conversationReport.styles';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { formatBotMessage } from '../../helper/aiRespondFormatter';
import { getConversationReport } from '../../services/publicChatBot';
import { Loading } from '../../components/uielements/loading/loading';
import imgUrbanIcon from '../../assets/PNG/urban_icon.png';

const ConversationReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [councilInfo, setCouncilInfo] = useState(null);

  const reportRef = useRef(null);
  const lastMessageRef = useRef(null);

  // Initialize on component mount
  useEffect(() => {
    const initializeReport = async () => {
      if (!id) {
        setError('No report ID found in URL');
        setIsLoading(false);
        return;
      }

      try {
        const response = await getConversationReport({
          sessionCode: id,
        });
        setCouncilInfo(response);
      } catch (error) {
        console.error('Error fetching conversation report:', error);
        setError('Failed to load conversation report');
      } finally {
        setIsLoading(false);
      }
    };

    initializeReport();
  }, [id]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${timeStr} • ${dateStr}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Download as PDF function
  const handleDownloadPDF = async () => {
    if (downloading) return;

    try {
      setDownloading(true);
      const reportElement = reportRef.current;
      if (!reportElement) {
        throw new Error('Report content not found');
      }

      const date = new Date();
      const filename = `Chat-Report-${date.toISOString().split('T')[0]}.pdf`;

      const canvas = await html2canvas(reportElement, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.7);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Loading />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <Main>
        <ContentContainer>
          <EmptyState>
            <MessageSquare />
            <h3>{error}</h3>
            <p>Please check the report ID and try again.</p>
            <ActionButton onClick={handleGoBack} style={{ marginTop: '24px' }}>
              <ArrowLeft style={{ width: '18px', height: '18px', marginBottom: 'unset' }} />
              Go Back
            </ActionButton>
          </EmptyState>
        </ContentContainer>
      </Main>
    );
  }

  return (
    <Main>
      <ContentContainer ref={reportRef}>
        <HeaderContainer>
          <HeaderTop>
            {councilInfo?.chatbotId?.logoUrl ? (
              <Logo
                fetchpriority="high"
                decoding="async"
                loading="eager"
                src={councilInfo?.chatbotId?.logoUrl}
                alt="Council Logo"
              />
            ) : (
              <div style={{ height: '48px' }} />
            )}
            <ButtonGroup>
              <ActionButton $variant="primary" onClick={handleDownloadPDF} disabled={downloading}>
                <Download />
                {downloading ? 'Downloading...' : 'Download'}
              </ActionButton>
            </ButtonGroup>
          </HeaderTop>

          <HeaderSection>
            <HeaderContent>
              <LogoCircle>
                <img src={imgUrbanIcon} alt="Urban Compass" />
              </LogoCircle>
              <Title>Conversation Report</Title>
              <Subtitle>Chat history with Urban Compass AI Assistant</Subtitle>
              {councilInfo?.chat?.length > 0 && (
                <InfoBadge>
                  <Calendar />
                  {formatDate(councilInfo.chat[0].createdAt)} • {councilInfo.chat.length} messages
                </InfoBadge>
              )}
            </HeaderContent>
          </HeaderSection>
        </HeaderContainer>

        <MessagesScrollContainer>
          {councilInfo?.chat && councilInfo.chat.length > 0 ? (
            councilInfo.chat.map((msg, index) => (
              <MessageContainer
                key={index}
                ref={index === councilInfo.chat.length - 1 ? lastMessageRef : null}
                $isUser={msg.role === 'USER'}
              >
                <MessageWrapper $isUser={msg.role === 'USER'}>
                  <MessageBubble $isUser={msg.role === 'USER'}>
                    {msg.role === 'BOT' ? formatBotMessage(msg.message) : msg.message}
                  </MessageBubble>
                  <TimeStamp $isUser={msg.role === 'USER'}>
                    {formatTimestamp(msg.createdAt)}
                  </TimeStamp>
                </MessageWrapper>
              </MessageContainer>
            ))
          ) : (
            <EmptyState>
              <MessageSquare />
              <h3>No messages found</h3>
              <p>This conversation report is empty.</p>
            </EmptyState>
          )}
        </MessagesScrollContainer>
      </ContentContainer>
    </Main>
  );
};

export default ConversationReportView;
